/**
 * Shared TipTap extension set for the Design tab's rich markdown surfaces —
 * the editable section editor (SectionWysiwyg) and the read-only previews
 * (RichMarkdown). Keeping one source means anything that renders in a preview
 * (tables, task lists, code blocks, …) also round-trips through the editor.
 *
 * `tiptap-markdown` handles markdown ⇄ ProseMirror (incl. GFM tables, which
 * the Table nodes below add to the schema). Returns a FRESH array per call so
 * each editor instance gets its own extension instances.
 */
import type { Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Markdown } from 'tiptap-markdown';
import { TagMentions, type SuggestTokenState } from './tag-mentions';

const TRUSTED_TYPES_POLICY_NAME = 'foundry-tiptap-markdown-domparser';
const DOM_PARSER_PATCH_FLAG = Symbol.for('foundry.tiptapMarkdownDomParserCompat');

interface TrustedTypesPolicyLike {
  createHTML(value: string): unknown;
}

interface TrustedTypesLike {
  createPolicy(name: string, rules: { createHTML(value: string): string }): TrustedTypesPolicyLike;
}

type TrustedTypesWindow = Window & { trustedTypes?: TrustedTypesLike };
type PatchedDOMParserPrototype = DOMParser & { [DOM_PARSER_PATCH_FLAG]?: true };

function installMarkdownDomParserTrustedTypesCompat(): void {
  if (typeof window === 'undefined') return;

  const trustedTypes = (window as TrustedTypesWindow).trustedTypes;
  if (!trustedTypes?.createPolicy || !window.DOMParser) return;

  const proto = window.DOMParser.prototype as PatchedDOMParserPrototype;
  if (proto[DOM_PARSER_PATCH_FLAG]) return;

  let policy: TrustedTypesPolicyLike;
  try {
    policy = trustedTypes.createPolicy(TRUSTED_TYPES_POLICY_NAME, {
      // tiptap-markdown renders with `html: false`; this policy only adapts
      // that escaped markdown-it HTML string to Chromium's DOMParser sink.
      createHTML: (value) => value,
    });
  } catch {
    return;
  }

  const parseFromString = proto.parseFromString;
  proto.parseFromString = function parseFromStringWithTrustedHTML(
    this: DOMParser,
    markup: string,
    mimeType: Parameters<DOMParser['parseFromString']>[1],
  ): ReturnType<DOMParser['parseFromString']> {
    const trustedMarkup =
      typeof markup === 'string' && mimeType === 'text/html' ? policy.createHTML(markup) : markup;
    return parseFromString.call(this, trustedMarkup as string, mimeType);
  } as DOMParser['parseFromString'];

  Object.defineProperty(proto, DOM_PARSER_PATCH_FLAG, {
    value: true,
    configurable: true,
  });
}

export interface RichExtensionOptions {
  /** Makes inline `#tag` tokens clickable (decoration-only; markdown untouched). */
  onTagClick?: (tag: string) => void;
  /** Makes inline `@Mention` tokens clickable (navigate to the named concept). */
  onMentionClick?: (name: string) => void;
  /** Autocomplete: reports the `#`/`@` token being typed at the cursor. */
  onSuggestToken?: (state: SuggestTokenState | null) => void;
}

export function buildRichExtensions(options: RichExtensionOptions = {}): Extensions {
  installMarkdownDomParserTrustedTypesCompat();

  return [
    StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
    Markdown.configure({
      html: false,
      tightLists: true,
      bulletListMarker: '-',
      linkify: true,
      breaks: false,
      transformPastedText: true,
      transformCopiedText: true,
    }),
    Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
    TaskList,
    TaskItem.configure({ nested: true }),
    // GFM tables — what makes preview/editor render `| a | b |` blocks as tables.
    Table.configure({ resizable: false }),
    TableRow,
    TableHeader,
    TableCell,
    // Inline `#tag` / `@Mention` references — styled always, clickable when wired.
    TagMentions.configure({
      onTagClick: options.onTagClick,
      onMentionClick: options.onMentionClick,
      onSuggestToken: options.onSuggestToken,
    }),
  ];
}
